import './CustomStatistics.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import moment from 'moment';

import { getAlarmStats, getCriticalAlarmsWithoutNotification, getAlarmHistoricalSummary, getAlarmStatsLoading } from 'src/module/alarm/selector';
import { getMetrics } from 'src/module/facility/selector';
import { getDashboardStats } from 'src/module/device/selector';

import { getNewWorkTickets } from 'src/module/workTicket/selector';
import { getClient, getPluralWorkTicketLabel, getCriticalityLabels } from 'src/module/authentication/selector';
import CustomStatistics from './CustomStatistics';
import workCompleted from 'src/assets/placeholders/work-completed.png';
import { useTranslation } from 'src/i18n';

export default function DashboardStatistics (props) {
  const { t } = useTranslation();

  const {
    defaultLayoutOverride,
  } = props;

  const client = useSelector(getClient);
  const alarmStats = useSelector(getAlarmStats);
  const metrics = useSelector(getMetrics);
  const dashboardStats = useSelector(getDashboardStats);
  const criticalAlarmsWithoutNotification = useSelector(getCriticalAlarmsWithoutNotification);
  const newWorkTickets = useSelector(getNewWorkTickets) as any;
  const alarmHistory = useSelector(getAlarmHistoricalSummary) as any;
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const alarmStatsLoading = useSelector(getAlarmStatsLoading);
  const lastReportSentAt = client.get('last_report_sent_at');
  const currentTemp = alarmStats.getIn(['currentSiteTemperature', 'current'], null);

  let circuitsLoto = null;

  const outstandingWorkOrders = alarmStats.getIn(['outstandingNotificationsByStatus', 'wo'], 0) + alarmStats.getIn(['outstandingNotificationsByStatus', 'eng_required'], 0);
  if (alarmStats) {
    circuitsLoto = alarmStats.getIn(['notesByHtc', 'loto']);
  }
  const variables = {
    alarmStats,
    alarmStatsLoading,
    client,
    metrics,
    totalHoursSavedToday: alarmHistory.getIn([alarmHistory.size - 1, 'total_auto_handled'], 'N/A'),
    totalNewNotifications: newWorkTickets.size,
    criticalAlarmsWithoutNotification,
    circuitsLoto,
    outstandingWorkOrders,
    date: moment().format('YYYY-MM-DD'),
    lastReportSentAt: lastReportSentAt ? moment(lastReportSentAt).format('YYYY-MM-DD') : 'N/A',
    currentSiteTemperature: currentTemp ? `${currentTemp}°C` : 'N/A',
    weekNumber: moment().isoWeek(),
    dashboardStats
  };

  const interpolatedVariables = {
    '%estTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estTotalMaintenaceHoursExplain',
      default: t('dashboard.totalEstimatedMaintenanceHours')
    },
    '%estBasicTotalMaintenanceHoursExplain%': {
      valuePath: 'alarmStats.estBasicTotalMaintenaceHoursExplain',
      default: t('dashboard.totalEstimatedMaintenanceHours')
    },
    '%mnraLabel%': {
      valuePath: 'client.preferences.notificationLabels.mnra',
      default: t('dashboard.ehtWalkDowns')
    },
    '%engRequiredLabel%': {
      valuePath: 'client.preferences.notificationLabels.eng_required',
      default: t('dashboard.engRequired')
    },
    '%workOrderLabel%': {
      valuePath: 'client.preferences.notificationLabels.wo',
      default: t('dashboard.scheduledRepairs')
    },
  };
  const devicesInAlarmWithoutNotifications = [];
  alarmStats?.get('devicesInAlarmWithoutNotificationByCriticality', []).forEach((value,  key) => {
    if (key > 0) {
      devicesInAlarmWithoutNotifications.push({
        type: 'minor',
        title: criticalityLabels?.get(key),
        helpText: t('workTickets.devicesInAlarmWithout', { priority: criticalityLabels?.get(key), label: pluralWorkTicketLabel || t('workTickets.title') }),
        value: `alarmStats.devicesInAlarmWithoutNotificationByCriticality.${key}`,
        defaultValue: 'N/A'
      });
    }
  });

  const dynamicChildren = {
    devicesInAlarmWithoutNotifications
  };

  const defaultLayout = [
    { "type": "grid", "container": true, "spacing": 3, "children": [
      { "type": "grid", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "component", "componentName": "WeatherForecast" }
      ] },

      { "type": "grid", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "block", "feature": "work-tickets", "title": t('dashboard.outlook'), "children": [
          { "type": "major", "title": t('dashboard.criticalAlarmsWithoutNotification'), "value": "alarmStats.criticalAlarmsWithoutNotification", "helpText": t('dashboard.criticalAlarmsWithoutNotificationHelp') },
          { "type": "major", "title": t('dashboard.newNotificationsLast7Days'), "value": "totalNewNotifications", "helpText": t('dashboard.newNotificationsLast7DaysHelp') },
          { "type": "major", "title": t('dashboard.circuitsForcedOn'), "value": "alarmStats.deviceStatusSummary.forced_on", "defaultValue": "N/A" },
          { "type": "major", "title": t('dashboard.fieldRepairsAssessments'), "value": "alarmStats.outstandingNotificationsByStatus.mnra", "helpText": t('dashboard.fieldRepairsAssessmentsHelp'), "defaultValue": 0 },
          { "type": "major", "title": t('dashboard.totalEstimatedMaintenance'), "value": "alarmStats.estBasicTotalMaintenaceHours", "helpText": "Based on site's average time to complete MNRA and WO and predictive failures: %estBasicTotalMaintenanceHoursExplain%", "defaultValue": 0 },
          { "type": "major", "title": t('dashboard.totalKwh'), "value": "dashboardStats.kwh", "helpText": t('dashboard.totalKwhHelp'), "defaultValue": 0, "dynamicTitle": "dynamickWhTitle", "formatFunction": "formatkWh", "dynamicHelpText": "dynamickWhHelpText" },
          { "type": "major", "title": t('dashboard.totalWasted'), "value": "dashboardStats.forcedOnKwh", "helpText": t('dashboard.totalWastedHelp'), "defaultValue": "-", "dynamicTitle": "dynamickWhForcedOnTitle", "formatFunction": "formatkWh", "dynamicHelpText": "dynamicForcedOnkWhHelpText" }
        ] }
      ] },

      { "type": "grid", "feature": "alarm-historian", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.alarmOverview'), "children": [
          { "type": "minor", "title": t('dashboard.totalAlarms'), "value": "alarmStats.totalAlarms", "helpText": t('dashboard.totalAlarmsHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.htcsInAlarm'), "value": "alarmStats.htcsInAlarm", "helpText": t('dashboard.htcsInAlarmHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.htcsInCritAlarm'), "value": "alarmStats.criticalAlarms", "helpText": t('dashboard.htcsInCritAlarmHelp'), "defaultValue": "N/A" }
        ] }
      ], fallback: [
        { "type": "block", className: 'no-permission-block', "title": t('dashboard.alarmOverview'), "children": [
          { "type": "minor", "title": t('dashboard.totalAlarms'), "value": "N/A", "helpText": t('dashboard.totalAlarmsHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.htcsInAlarm'), "value": "N/A", "helpText": t('dashboard.htcsInAlarmHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.htcsInCritAlarm'), "value": "N/A", "helpText": t('dashboard.htcsInCritAlarmHelp'), "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "feature": "alarm-validation", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.systemOverviewBlock'), "children": [
          { "type": "minor", "title": t('dashboard.nuisanceAlarms'), "value": "alarmStats.notesByHtc.nuisance", "helpText": t('dashboard.nuisanceAlarmsHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.loto'), "value": "alarmStats.notesByHtc.loto", "helpText": t('dashboard.lotoHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.offlineDevices'), "value": "alarmStats.deviceStatusSummary.offline", "helpText": t('dashboard.offlineDevicesHelp'), "defaultValue": "N/A" }
        ] }
      ], fallback: [
        { "type": "block", "className": "no-permission-block", "title": t('dashboard.systemOverviewBlock'), "children": [
          { "type": "minor", "title": t('dashboard.nuisanceAlarms'), "value": "N/A", "helpText": t('dashboard.nuisanceAlarmsHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.loto'), "value": "N/A", "helpText": t('dashboard.lotoHelp'), "defaultValue": "N/A" },
          { "type": "minor", "title": t('dashboard.offlineDevices'), "value": "N/A", "helpText": t('dashboard.offlineDevicesHelp'), "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "feature": "erp-integration", "container": false, "md": 4, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.erpOverview'), "children": [
          { "type": "minor", "title": "%mnraLabel%", "value": "alarmStats.outstandingNotificationsByStatus.mnra", "helpText": "Total open SAP tickets requiring engineering", "defaultValue": "N/A" },
          { "type": "minor", "title": "%engRequiredLabel%", "value": "alarmStats.outstandingNotificationsByStatus.eng_required", "helpText": "Total open SAP Work Orders", "defaultValue": "N/A" },
          { "type": "minor", "title": "%workOrderLabel%", "value": "alarmStats.totalNotificationsWithWorkOrders", "helpText": "Total open SAP notifications for an approved minor repair", "defaultValue": "N/A" }
        ] }
      ], fallback: [
        { "type": "block", "className": "no-permission-block", "title": t('dashboard.erpOverview'), "children": [
          { "type": "minor", "title": "%mnraLabel%", "value": "N/A", "defaultValue": "N/A" },
          { "type": "minor", "title": "%engRequiredLabel%", "value": "N/A", "defaultValue": "N/A" },
          { "type": "minor", "title": "%workOrderLabel%", "value": "N/A", "defaultValue": "N/A" }
        ] }
      ] },

      { "type": "grid", "feature": "advanced-metrics", "container": false, "md": 12, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.sensorHistory'), "children": [
          { "type": "component", "componentName": "SensorHistory" }
        ] }
      ] },

      { "type": "grid", "feature": "work-tickets", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.workCompleted'), "children": [
          { "type": "component", "componentName": "WorkCompletedGraph" }
        ] }
      ], fallback: [
        { "type": "block", "className": "no-permission-block", "title": t('dashboard.workCompleted'), "children": [
          { "type": "image", "image": workCompleted, "style": { padding: '8px 0' } },
        ] }
      ] },

      { "type": "grid", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.alarmsByStatus'), "children": [
          { "type": "component", "componentName": "AlarmsByStatusGraph" }
        ] }
      ] },

      { "type": "grid", "feature": "work-tickets", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.newWorkTickets'), "children": [
          { "type": "component", "componentName": "NewWorkTickets" }
        ] }
      ], fallback: [
        { "type": "block", "className": "no-permission-block", "title": t('dashboard.newWorkTickets'), "children": [
          { "type": "major", "title": t('dashboard.newWorkTicketsToday'), "value": "N/A", "defaultValue": "N/A" },
          { "type": "major", "title": t('dashboard.workTicketsClosed'), "value": "N/A", "defaultValue": "N/A" },
        ] }
      ] },

      { "type": "grid", "feature": "criticality-ruleset", "container": false, "md": 6, "xs": 12, "children": [
        { "type": "block", "title": t('dashboard.criticalAlarms'), "children": [
          { "type": "component", "componentName": "CriticalAlarms" }
        ] }
      ], fallback: [
        { "type": "block", "className": "no-permission-block", "title": t('dashboard.criticalAlarms'), "children": [
          { "type": "major", "title": t('dashboard.newCriticalAlarmsToday'), "value": "N/A", "defaultValue": "N/A" },
          { "type": "major", "title": t('dashboard.alarmsComparedToYesterday'), "value": "N/A", "defaultValue": "N/A" },
        ] }
      ] }
    ] }
  ];

  return (
    <CustomStatistics
      layoutPreferencePath={props.layoutPreferencePath || 'client.preferences.dashboard.layout'}
      variables={variables}
      interpolatedVariables={interpolatedVariables}
      defaultLayout={defaultLayoutOverride ? defaultLayoutOverride : defaultLayout}
      dynamicChildren={dynamicChildren}
    />
  );
}

DashboardStatistics.propTypes = {
  layoutPreferencePath: PropTypes.string,
  defaultLayoutOverride: PropTypes.array,
};

DashboardStatistics.defaultProps = {
  layoutPreferencepPath: null,
  defaultLayoutOverride: null
};
